import {
  clearCustomCursorMagnet,
  setCustomCursorIntensity,
  setCustomCursorMagnetTarget,
} from './customCursor';

type FieldKey = 'name' | 'email' | 'message';

let activeForm: HTMLFormElement | null = null;
let sendBtn: HTMLButtonElement | null = null;

export function bindContactForm(form: HTMLFormElement | null): () => void {
  if (!form) return () => undefined;

  activeForm = form;
  sendBtn = form.querySelector<HTMLButtonElement>('.send-btn');

  const onSubmit = (e: Event): void => {
    clearErrors(form);
    if (!isFormFilled(form)) {
      e.preventDefault();
      e.stopPropagation();
      explodeFormFields(form);
      return;
    }

    const name = form.querySelector<HTMLInputElement>('#contact-name');
    const email = form.querySelector<HTMLInputElement>('#contact-email');
    const message = form.querySelector<HTMLTextAreaElement>('#contact-message');
    let ok = true;

    if (!name?.value.trim()) {
      setError(form, 'name', 'Name is required');
      ok = false;
    }
    if (!email?.value.trim() || !email.checkValidity()) {
      setError(form, 'email', 'Enter a valid email');
      ok = false;
    }
    if (!message?.value.trim()) {
      setError(form, 'message', 'Message is required');
      ok = false;
    }

    if (!ok) {
      e.preventDefault();
      e.stopPropagation();
      explodeFormFields(form);
    }
  };

  const onSendClick = (e: MouseEvent): void => {
    if (!isFormFilled(form)) {
      e.preventDefault();
      e.stopPropagation();
      explodeFormFields(form);
      return;
    }
    // Valid — native submit + unicorn handler proceed.
  };

  form.addEventListener('submit', onSubmit);
  sendBtn?.addEventListener('click', onSendClick);

  return () => {
    form.removeEventListener('submit', onSubmit);
    sendBtn?.removeEventListener('click', onSendClick);
    if (activeForm === form) {
      activeForm = null;
      sendBtn = null;
      clearCustomCursorMagnet();
    }
  };
}

/** Magnetic pull toward Send while the pointer is over the form. */
export function updateSendMagnet(clientX: number, clientY: number): number {
  if (!sendBtn || !activeForm) {
    clearCustomCursorMagnet();
    return 0;
  }

  const formRect = activeForm.getBoundingClientRect();
  const rect = sendBtn.getBoundingClientRect();
  const tx = rect.left + rect.width * 0.5;
  const ty = rect.top + rect.height * 0.5;
  const dist = Math.hypot(clientX - tx, clientY - ty);
  const maxDist = Math.max(240, rect.width * 3.8);
  const inSendBand = clientY >= formRect.top + formRect.height * 0.52;
  if (dist >= maxDist || (!inSendBand && dist > maxDist * 0.62)) {
    clearCustomCursorMagnet();
    return 0;
  }

  const t = 1 - dist / maxDist;
  const strength = t * t * (3 - 2 * t);
  setCustomCursorMagnetTarget(tx, ty, strength);
  setCustomCursorIntensity(0.28 + strength * 0.62, true);
  return strength;
}

export function isPointerOverContactForm(clientX: number, clientY: number): boolean {
  const hit = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
  return !!hit?.closest?.('.contact-form');
}

function isFormFilled(form: HTMLFormElement): boolean {
  const name = form.querySelector<HTMLInputElement>('#contact-name')?.value.trim();
  const email = form.querySelector<HTMLInputElement>('#contact-email')?.value.trim();
  const message = form.querySelector<HTMLTextAreaElement>('#contact-message')?.value.trim();
  if (!name || !message) return false;
  const emailEl = form.querySelector<HTMLInputElement>('#contact-email');
  return !!email && !!emailEl?.checkValidity();
}

function explodeFormFields(form: HTMLFormElement): void {
  const keys: FieldKey[] = ['name', 'email', 'message'];
  form.classList.remove('is-field-explode');
  void form.offsetWidth;
  form.classList.add('is-field-explode');

  for (const key of keys) {
    const input = form.querySelector<HTMLInputElement | HTMLTextAreaElement>(`#contact-${key}`);
    const field = input?.closest('.field') as HTMLElement | null;
    if (!field) continue;

    const empty =
      key === 'email'
        ? !input?.value.trim() || !(input as HTMLInputElement).checkValidity()
        : !input?.value.trim();

    field.classList.toggle('is-field-empty', empty);
    if (empty) {
      field.style.setProperty('--field-burst-x', `${(Math.random() - 0.5) * 48}px`);
      field.style.setProperty('--field-burst-y', `${-10 - Math.random() * 26}px`);
      field.style.setProperty('--field-burst-r', `${(Math.random() - 0.5) * 14}deg`);
      setError(form, key, fieldErrorText(key));
    } else {
      field.classList.remove('is-field-empty');
    }
  }

  window.setTimeout(() => form.classList.remove('is-field-explode'), 680);
}

function fieldErrorText(key: FieldKey): string {
  if (key === 'email') return 'Enter a valid email';
  if (key === 'name') return 'Name is required';
  return 'Message is required';
}

function setError(form: HTMLFormElement, key: string, text: string): void {
  const el = form.querySelector<HTMLElement>(`.invalid-feedback[data-for="${key}"]`);
  if (!el) return;
  el.hidden = false;
  el.textContent = text;
}

function clearErrors(form: HTMLFormElement): void {
  form.querySelectorAll<HTMLElement>('.invalid-feedback').forEach((el) => {
    el.hidden = true;
    el.textContent = '';
  });
}
